import { supabase } from './supabaseClient'

class SupabaseSystemConfigService {
    constructor() {
        this.tableName = 'system_configs'
    }

    /**
     * 获取所有配置项
     */
    async getAllConfigs() {
        try {
            const { data, error } = await supabase
                .from(this.tableName)
                .select('*')
                .order('config_key', { ascending: true })

            if (error) {
                throw error
            }

            return data || []
        } catch (error) {
            console.error('获取配置失败:', error)
            throw error
        }
    }

    /**
     * 根据访问角色获取配置项
     * @param {string} role - 用户角色 ('user' | 'admin')
     */
    async getConfigsByRole(role = 'user') {
        try {
            const { data, error } = await supabase
                .from(this.tableName)
                .select('*')
                .eq('access_role', role)
                .order('config_key', { ascending: true })

            if (error) {
                throw error
            }

            return data || []
        } catch (error) {
            console.error('获取角色配置失败:', error)
            throw error
        }
    }

    /**
     * 获取单个配置项
     * @param {string} configKey - 配置键名
     */
    async getConfig(configKey) {
        try {
            const { data, error } = await supabase
                .from(this.tableName)
                .select('*')
                .eq('config_key', configKey)
                .single()

            if (error) {
                if (error.code === 'PGRST116') {
                    // 配置项不存在
                    return null
                }
                throw error
            }

            return data
        } catch (error) {
            console.error('获取配置失败:', error)
            throw error
        }
    }

    /**
     * 获取配置值
     * @param {string} configKey - 配置键名
     * @param {any} defaultValue - 默认值
     */
    async getConfigValue(configKey, defaultValue = null) {
        try {
            const config = await this.getConfig(configKey)
            return config ? config.config_value : defaultValue
        } catch (error) {
            console.error('获取配置值失败:', error)
            return defaultValue
        }
    }

    /**
     * 批量获取配置值
     * @param {string[]} configKeys - 配置键名数组
     */
    async getConfigValues(configKeys) {
        try {
            const { data, error } = await supabase
                .from(this.tableName)
                .select('config_key, config_value')
                .in('config_key', configKeys)

            if (error) {
                throw error
            }

            // 转换为键值对对象
            const configMap = {}
            data?.forEach(item => {
                configMap[item.config_key] = item.config_value
            })

            return configMap
        } catch (error) {
            console.error('批量获取配置值失败:', error)
            throw error
        }
    }

    /**
     * 创建配置项
     * @param {Object} configData - 配置数据
     */
    async createConfig(configData) {
        try {
            const { data, error } = await supabase
                .from(this.tableName)
                .insert([{
                    config_key: configData.config_key,
                    config_value: configData.config_value,
                    access_role: configData.access_role || 'admin',
                    description: configData.description || ''
                }])
                .select()
                .single()

            if (error) {
                throw error
            }

            return data
        } catch (error) {
            console.error('创建配置失败:', error)
            throw error
        }
    }

    /**
     * 更新配置项
     * @param {string} configKey - 配置键名
     * @param {Object} updateData - 更新数据
     */
    async updateConfig(configKey, updateData) {
        try {
            const { data, error } = await supabase
                .from(this.tableName)
                .update({
                    config_value: updateData.config_value,
                    access_role: updateData.access_role,
                    description: updateData.description
                })
                .eq('config_key', configKey)
                .select()
                .single()

            if (error) {
                throw error
            }

            return data
        } catch (error) {
            console.error('更新配置失败:', error)
            throw error
        }
    }

    /**
     * 设置配置值（如果不存在则创建，存在则更新）
     * @param {string} configKey - 配置键名
     * @param {any} configValue - 配置值
     * @param {string} accessRole - 访问角色
     * @param {string} description - 描述
     */
    async setConfig(configKey, configValue, accessRole = 'admin', description = '') {
        try {
            // 先尝试获取现有配置
            const existingConfig = await this.getConfig(configKey)

            if (existingConfig) {
                // 更新现有配置
                return await this.updateConfig(configKey, {
                    config_value: configValue,
                    access_role: accessRole,
                    description: description
                })
            } else {
                // 创建新配置
                return await this.createConfig({
                    config_key: configKey,
                    config_value: configValue,
                    access_role: accessRole,
                    description: description
                })
            }
        } catch (error) {
            console.error('设置配置失败:', error)
            throw error
        }
    }

    /**
     * 批量设置配置
     * @param {Array} configs - 配置数组
     */
    async setConfigs(configs) {
        try {
            const results = []
            for (const config of configs) {
                const result = await this.setConfig(
                    config.config_key,
                    config.config_value,
                    config.access_role,
                    config.description
                )
                results.push(result)
            }
            return results
        } catch (error) {
            console.error('批量设置配置失败:', error)
            throw error
        }
    }

    /**
     * 删除配置项
     * @param {string} configKey - 配置键名
     */
    async deleteConfig(configKey) {
        try {
            const { error } = await supabase
                .from(this.tableName)
                .delete()
                .eq('config_key', configKey)

            if (error) {
                throw error
            }

            return true
        } catch (error) {
            console.error('删除配置失败:', error)
            throw error
        }
    }

    /**
     * 获取对象存储配置
     */
    async getStorageConfig() {
        try {
            return await this.getConfigValue('storage.config', {})
        } catch (error) {
            console.error('获取存储配置失败:', error)
            throw error
        }
    }

    /**
     * 保存对象存储配置
     * @param {Object} storageConfig - 存储配置对象
     */
    async saveStorageConfig(storageConfig) {
        try {
            // 将整个存储配置作为JSON保存到一个字段
            return await this.setConfig(
                'storage.config',
                storageConfig,
                'admin',
                '对象存储完整配置'
            )
        } catch (error) {
            console.error('保存存储配置失败:', error)
            throw error
        }
    }

    /**
     * 获取网站配置
     */
    async getSiteConfig() {
        try {
            return await this.getConfigValue('site.config', {})
        } catch (error) {
            console.error('获取网站配置失败:', error)
            throw error
        }
    }


}

export default new SupabaseSystemConfigService() 